/**
 * Use promotional grid to surface and feature more than one promotional card at a time without using a rail
 *
 * @module views/components/PromotionalGrid
 * @memberof -Common
 */
import './PromotionalGrid.scss';

import React from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';

import GridContainer from '@ulta/core/components/GridContainer/GridContainer';
import Text from '@ulta/core/components/Text/Text';
import { useDeviceInflection } from '@ulta/core/providers/InflectionProvider/InflectionProvider';
import { spreadProps } from '@ulta/core/utils/dom/dom';

import PromotionalCard from '@ulta/modules/PromotionalCard/PromotionalCard';

/**
 * Represents a PromotionalGrid component
 *
 * @method
 * @param {PromotionalGridProps} props - React properties passed from composition
 * @returns PromotionalGrid
 */
export const PromotionalGrid = function( props ){
  const {
    alignment,
    backgroundColor,
    items,
    subtitle,
    title
  } = props;

  const { breakpoint } = useDeviceInflection();
  const isLargeDevice = breakpoint?.isLargeDevice();

  const getCardAlignment = ( index, alignment ) => {
    let newAlignment = alignment;
    const twoCardLayout = ( ( items.length ) === 2 );
    const fiveCardLayout = ( ( ( items.length ) === 5 ) && ( ( index === 3 ) || ( index === 4 ) ) );
    const sixCardLayout = ( ( ( items.length ) === 6 ) && ( ( index === 0 ) || ( index === 1 ) ) );

    switch ( true ){
      case !isLargeDevice:
        newAlignment = 'vertical';
        break;
      case twoCardLayout:
        newAlignment = 'vertical';
        break;
      case fiveCardLayout:
        newAlignment = 'vertical';
        break;
      case sixCardLayout:
        newAlignment = 'vertical';
        break;
      default:
        break;
    }
    return newAlignment;
  };

  if( !items ){
    return null;
  }

  return (
    <div className={
      classNames( 'PromotionalGrid', {
        'PromotionalGrid__withBackgroundColor': backgroundColor,
        [`PromotionalGrid__with${items.length}Items`]: items.length
      } )
    }
    >
      <GridContainer
        { ...spreadProps( {
          fullBleedContentBackgroundColor: backgroundColor
        } ) }
      >
        { title &&
        <div className={
          classNames( 'PromotionalGrid__title', {
            'PromotionalGrid__title--noSubtitle': !subtitle
          } )
        }
        >
          <Text
            textStyle='title-4'
            htmlTag='h2'
          >
            { title }
          </Text>
        </div>
        }
        { subtitle &&
        <div className='PromotionalGrid__subTitle'>
          <Text
            textStyle='subtitle-1'
            htmlTag='h3'
          >
            { subtitle }
          </Text>
        </div>
        }
        { items.map( ( item, index ) =>(
          <div
            className={ `PromotionalGrid__items PromotionalGrid__items--${index + 1}` }
            key={ `PromotionalGridItems${index}` }
          >
            <PromotionalCard
              { ...spreadProps( { ...item } ) }
              alignment={ getCardAlignment( index, alignment ) }
            />
          </div>
        ) )
        }
      </GridContainer>
    </div>
  );
};

/**
 * Property type definitions
 * @typedef PromotionalGridProps
 * @type {object}
 * @property {string} title - Sets the headline title.
 * @property {string} subtitle - Sets the subtitle.
 * @property {array} items - Set the Item array.
 * @property {string} backgroundColor - Sets the backgroundColor
 * @property {string} alignment - sets the alignment for PromotionalCard component.
 */
export const propTypes =  {
  title: PropTypes.string,
  subtitle: PropTypes.string,
  items: PropTypes.array,
  backgroundColor: PropTypes.string,
  alignment: PropTypes.string
};

PromotionalGrid.propTypes = propTypes;

export default PromotionalGrid;
